import {
  DoubleRightOutlined,
  DownOutlined,
  LinkOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { GET_USER_BASICS_BY_ID, User } from '@frontend/data-access';
import { ProtectedCard, UserAvatar, UserInfo } from '@frontend/shared/ui';
import { Avatar, Button, Drawer, Dropdown, Grid, Skeleton, Spin } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import Link from 'next/link';
import { useState } from 'react';

const { useBreakpoint } = Grid;

/* eslint-disable-next-line */
export interface ApplicantsProps {
  users: User[];
  anonymous: boolean;
}

export function Applicants({ users, anonymous }: ApplicantsProps) {
  const { t: tcommon } = useTranslation('common');
  const [drawer, setDrawer] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [getUserInfo, { data: userData, loading }] = useLazyQuery<{
    findUser: User;
  }>(GET_USER_BASICS_BY_ID);
  const screens = useBreakpoint();

  const getData = (id: string) => {
    getUserInfo({
      variables: {
        id: id,
      },
    });
  };

  const handleDrawer = () => {
    setDrawer(!drawer);
  };

  const handleClick = (user: User) => {
    setUser(user);
    handleDrawer();
    getData(user?.id.toString());
  };

  const ApplicantsBuilder = () => {
    if (users.length === 0) {
      return (
        <div className="flex gap-3 items-center">
          <Avatar size={32} icon={<UserOutlined />} />
          <span className="text-gray-800 text-sm font-normal">
            {tcommon('anonymous')}
          </span>
        </div>
      );
    } else if (users.length > 1) {
      return (
        <Dropdown
          menu={{
            items: [
              ...users.map((user, index) => {
                if (index === 0 && anonymous) {
                  return {
                    key: index,
                    label: (
                      <div className="flex items-center gap-2 py-1">
                        <Avatar size={32} icon={<UserOutlined />} />
                        <span>{tcommon('anonymous')}</span>
                      </div>
                    ),
                  };
                }
                return {
                  key: index,
                  label: (
                    <div
                      className="flex items-center gap-2 py-1"
                      onClick={() => handleClick(user)}
                    >
                      <UserAvatar
                        size={32}
                        user={user}
                        showBadge
                        isClickable={false}
                      />
                      <span className="truncate">{user.fullName}</span>
                    </div>
                  ),
                };
              }),
            ],
          }}
          trigger={['click']}
          placement="bottomLeft"
        >
          <div className="flex gap-1 items-center p-1 hover:bg-gray-300 rounded-full cursor-pointer">
            {anonymous || !users[0] ? (
              <Avatar size={32} icon={<UserOutlined />} />
            ) : (
              users[0] && (
                <UserAvatar
                  size={32}
                  user={users[0]}
                  showBadge
                  isClickable={false}
                />
              )
            )}
            {users.slice(1, 3).map((user, index) => (
              <UserAvatar
                key={index}
                size={32}
                user={user}
                showBadge
                isClickable={false}
              />
            ))}
            {users.length === 4 && (
              <UserAvatar
                size={32}
                user={users[3]}
                showBadge
                isClickable={false}
              />
            )}
            {users.length > 4 && (
              <Avatar size="default" shape="circle" className="bg-gray-600">
                +{users.length - 3}
              </Avatar>
            )}
            <DownOutlined className="text-gray-600 mx-2" />
          </div>
        </Dropdown>
      );
    } else {
      return (
        <div
          className="flex gap-3 items-center p-1 pr-3 hover:bg-gray-300 rounded-full cursor-pointer max-w-56"
          onClick={() => handleClick(users[0])}
        >
          {users[0] && (
            <UserAvatar
              size={32}
              user={users[0]}
              showBadge
              isClickable={false}
            />
          )}
          <span className="text-gray-800 text-sm font-normal truncate">
            {users[0].fullName}
          </span>
        </div>
      );
    }
  };

  return (
    <div>
      <ApplicantsBuilder />
      <Drawer
        title={
          <div className="flex justify-between">
            <Button
              ghost
              type="text"
              onClick={handleDrawer}
              icon={<DoubleRightOutlined />}
            />
            <Link href={`/user/${user?.id}`} target="_blank">
              <Button
                type="primary"
                shape="round"
                onClick={handleDrawer}
                icon={<LinkOutlined />}
              >
                {tcommon('user_profile_open_button')}
              </Button>
            </Link>
          </div>
        }
        placement="right"
        closable={false}
        onClose={handleDrawer}
        open={drawer}
        width={screens.md ? '500' : undefined}
      >
        <ProtectedCard type="MEMBER">
          {loading ? (
            <div className="text-center">
              <Spin />
            </div>
          ) : userData?.findUser ? (
            <UserInfo user={userData?.findUser} />
          ) : (
            <div className="space-y-4 text-center">
              <Avatar size={70} src="/data/avatar-1.jpeg" />
              <Skeleton />
              <Skeleton />
            </div>
          )}
        </ProtectedCard>
      </Drawer>
    </div>
  );
}

export default Applicants;
